/**
 * Copyright (c) 2020 Google Inc
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 * the Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
 * FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
 * IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
 * CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
import scrollIntoView from 'scroll-into-view-if-needed'
import Fuse from 'fuse.js'

const exposed = {};
if (location.search) {
  var a = document.createElement("a");
  a.href = location.href;
  a.search = "";
  history.replaceState(null, null, a.href);
}

function prefetch(e) {
  if (e.target.tagName != "A") {
    return;
  }
  if (e.target.origin != location.origin) {
    return;
  }
  /**
   * Return the given url with no fragment
   * @param {string} url potentially containing a fragment
   * @return {string} url without fragment
   */
  const removeUrlFragment = (url) => url.split("#")[0];
  if (removeUrlFragment(window.location.href) === removeUrlFragment(e.target.href)) {
    return;
  }
  var l = document.createElement("link");
  l.rel = "prefetch";
  l.href = e.target.href;
  document.head.appendChild(l);
}
document.documentElement.addEventListener("mouseover", prefetch, {
  capture: true,
  passive: true,
});
document.documentElement.addEventListener("touchstart", prefetch, {
  capture: true,
  passive: true,
});

const GA_ID = document.documentElement.getAttribute("ga-id");
window.ga =
  window.ga ||
  function () {
    if (!GA_ID) {
      return;
    }
    (ga.q = ga.q || []).push(arguments);
  };
ga.l = +new Date();
ga("create", GA_ID, "auto");
ga("set", "transport", "beacon");
var timeout = setTimeout(
  (onload = function () {
    clearTimeout(timeout);
    ga("send", "pageview");
  }),
  1000
);

var ref = +new Date();
function ping(event) {
  var now = +new Date();
  if (now - ref < 1000) {
    return;
  }
  ga("send", {
    hitType: "event",
    eventCategory: "page",
    eventAction: event.type,
    eventLabel: Math.round((now - ref) / 1000),
  });
  ref = now;
}
addEventListener("pagehide", ping);
addEventListener("visibilitychange", ping);

/**
 * Injects a script into document.head
 * @param {string} src path of script to be injected in <head>
 * @return {Promise} Promise object that resolves on script load event
 */
const dynamicScriptInject = (src) => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.type = "text/javascript";
    document.head.appendChild(script);
    script.addEventListener("load", () => {
      resolve(script);
    });
  });
};

// Script web-vitals.js will be injected dynamically if user opts-in to sending CWV data.
const sendWebVitals = document.currentScript.getAttribute("data-cwv-src");

if (/web-vitals.js/.test(sendWebVitals)) {
  dynamicScriptInject(`${window.location.origin}/js/web-vitals.js`)
  .then(() => {
    webVitals.getCLS(sendToGoogleAnalytics);
    webVitals.getFID(sendToGoogleAnalytics);
    webVitals.getLCP(sendToGoogleAnalytics);
  })
  .catch((error) => {
    console.error(error);
  });
}

addEventListener(
  "click",
  function (e) {
    var button = e.target.closest("button");
    if (!button) {
      return;
    }
    ga("send", {
      hitType: "event",
      eventCategory: "button",
      eventAction: button.getAttribute("aria-label") || button.textContent,
    });
  },
  true
);
var selectionTimeout;
addEventListener(
  "selectionchange",
  function () {
    clearTimeout(selectionTimeout);
    var text = String(document.getSelection()).trim();
    if (text.split(/[\s\n\r]+/).length < 3) {
      return;
    }
    selectionTimeout = setTimeout(function () {
      ga("send", {
        hitType: "event",
        eventCategory: "selection",
        eventAction: text,
      });
    }, 2000);
  },
  true
);

function setHiddenText(el, text) {
  const value = (text || "").trim();
  el.textContent = value;
  el.hidden = !value;
}

function setHiddenHtml(el, html) {
  const value = (html || "").trim();
  el.innerHTML = value;
  el.hidden = !value;
}

function openModal(article) {
  const modal = document.getElementById("modal");
  const modalSheet = modal.querySelector(".modal__sheet");
  if (pendingCloseHandler) {
    modalSheet.removeEventListener("transitionend", pendingCloseHandler);
    pendingCloseHandler = null;
  }
  const modalImage = document.getElementById("modalImage");
  const modalFrameBadge = document.getElementById("modalFrameBadge");
  modalImage.querySelector("picture")?.remove();
  const picture = article.querySelector("picture");
  if (picture) {
    // Sharp square crop — object-fit:cover centers and crops any source
    // aspect ratio to the 1:1 media box. Keep the inline LQIP placeholder
    // style so the existing capture-phase "load" listener (below) still
    // clears it once the real <img> loads, preserving the blur-up transition.
    const clonedPicture = picture.cloneNode(true);
    const img = clonedPicture.querySelector("img");
    img.removeAttribute("width");
    img.removeAttribute("height");
    img.className = "modal__media-img";
    modalImage.appendChild(clonedPicture);
  }
  modalImage.hidden = !picture;

  modal.classList.toggle("modal--no-image", !picture);

  const titleEl = article.querySelector(".title");
  let titleHtml = "";
  if (titleEl) {
    const titleClone = titleEl.cloneNode(true);
    titleClone.querySelectorAll(".subtitle").forEach((el) => el.remove());
    titleHtml = titleClone.innerHTML;
  }
  document.getElementById("modalTitle").innerHTML = titleHtml;

  const subtitleEl = article.querySelector(".subtitle");
  setHiddenText(document.getElementById("modalSubtitle"), subtitleEl && subtitleEl.textContent);

  const grupEl = article.querySelector(".grup-label");
  setHiddenText(document.getElementById("modalGrup"), grupEl && grupEl.textContent);

  const priceMainEl = article.querySelector(".price-main");
  document.getElementById("modalPrice").innerHTML = priceMainEl ? priceMainEl.innerHTML : "";
  modalFrameBadge.innerHTML = priceMainEl ? priceMainEl.innerHTML : "";

  const pricePairs = Array.from(article.querySelectorAll(".price-pair"))
    .map((pair) => {
      const labelEl = pair.querySelector(".title-ext");
      const valueEl = pair.querySelector(".price-ext");
      if (!valueEl) {
        return "";
      }
      const label = labelEl ? labelEl.textContent.trim() : "";
      return (label ? `<span class="modal__price-ext-label">${label}</span> ` : "") + valueEl.innerHTML.trim();
    })
    .filter(Boolean);
  setHiddenHtml(document.getElementById("modalPriceExt"), pricePairs.join(" · "));

  const excerptEl = article.querySelector(".excerpt");
  const modalExcerpt = document.getElementById("modalExcerpt");
  modalExcerpt.innerHTML = "";
  if (excerptEl) {
    modalExcerpt.appendChild(excerptEl.cloneNode(true));
  }
  modalExcerpt.hidden = !excerptEl;

  modalTriggerEl = article;
  modal.hidden = false;
  document.body.classList.add("modal-open");
  requestAnimationFrame(() => modal.classList.add("is-open"));
  modalSheet.focus();
}

function closeModal() {
  const modal = document.getElementById("modal");
  const modalSheet = modal.querySelector(".modal__sheet");
  if (modal.hidden) {
    return;
  }
  if (pendingCloseHandler) {
    modalSheet.removeEventListener("transitionend", pendingCloseHandler);
  }
  modal.classList.remove("is-open");
  pendingCloseHandler = (e) => {
    if (e.target !== modalSheet) {
      return;
    }
    modalSheet.removeEventListener("transitionend", pendingCloseHandler);
    pendingCloseHandler = null;
    modal.hidden = true;
    document.body.classList.remove("modal-open");
    if (modalTriggerEl) {
      modalTriggerEl.tabIndex = -1;
      modalTriggerEl.focus({ preventScroll: true });
      modalTriggerEl = null;
    }
  };
  modalSheet.addEventListener("transitionend", pendingCloseHandler);
}
expose("closeModal", closeModal)

function openInfoModal(header) {
  const infoModal = document.getElementById("infoModal");
  const infoSheet = infoModal.querySelector(".info-modal__sheet");
  if (pendingInfoCloseHandler) {
    infoSheet.removeEventListener("transitionend", pendingInfoCloseHandler);
    pendingInfoCloseHandler = null;
  }
  infoModalTriggerEl = header;
  infoModal.hidden = false;
  document.body.classList.add("modal-open");
  requestAnimationFrame(() => infoModal.classList.add("is-open"));
  infoSheet.focus();
}

function closeInfoModal() {
  const infoModal = document.getElementById("infoModal");
  const infoSheet = infoModal.querySelector(".info-modal__sheet");
  if (infoModal.hidden) {
    return;
  }
  if (pendingInfoCloseHandler) {
    infoSheet.removeEventListener("transitionend", pendingInfoCloseHandler);
  }
  infoModal.classList.remove("is-open");
  pendingInfoCloseHandler = (e) => {
    if (e.target !== infoSheet) {
      return;
    }
    infoSheet.removeEventListener("transitionend", pendingInfoCloseHandler);
    pendingInfoCloseHandler = null;
    infoModal.hidden = true;
    document.body.classList.remove("modal-open");
    if (infoModalTriggerEl) {
      infoModalTriggerEl.tabIndex = -1;
      infoModalTriggerEl.focus({ preventScroll: true });
      infoModalTriggerEl = null;
    }
  };
  infoSheet.addEventListener("transitionend", pendingInfoCloseHandler);
}
expose("openInfoModal", openInfoModal)
expose("closeInfoModal", closeInfoModal)

let modalTriggerEl = null;
let pendingCloseHandler = null;
let infoModalTriggerEl = null;
let pendingInfoCloseHandler = null;

addEventListener("keydown", (e) => {
  if (e.key === "Escape") {
    const modal = document.getElementById("modal");
    const infoModal = document.getElementById("infoModal");
    if (!modal.hidden) {
      closeModal();
    } else if (!infoModal.hidden) {
      closeInfoModal();
    }
  }
})

let dragStartY = null;
let dragCurrentY = 0;
let dragModalId = null;

addEventListener("pointerdown", (e) => {
  let modalHandle = e.target.closest(".modal__handle");
  if (modalHandle) {
    dragModalId = "modal";
    dragStartY = e.clientY;
    const modalSheet = document.getElementById("modal").querySelector(".modal__sheet");
    modalSheet.classList.add("is-dragging");
    modalHandle.setPointerCapture(e.pointerId);
    return;
  }
  modalHandle = e.target.closest(".info-modal__handle");
  if (modalHandle) {
    dragModalId = "infoModal";
    dragStartY = e.clientY;
    const infoSheet = document.getElementById("infoModal").querySelector(".info-modal__sheet");
    infoSheet.classList.add("is-dragging");
    modalHandle.setPointerCapture(e.pointerId);
    return;
  }
});

function getDragSheet() {
  const selector = dragModalId === "modal" ? ".modal__sheet" : ".info-modal__sheet";
  return document.getElementById(dragModalId).querySelector(selector);
}

addEventListener("pointermove", (e) => {
  if (dragStartY === null) {
    return;
  }
  const sheet = getDragSheet();
  dragCurrentY = Math.max(0, e.clientY - dragStartY);
  sheet.style.transform = `translateY(${dragCurrentY}px)`;
});

addEventListener("pointerup", () => {
  if (dragStartY === null) {
    return;
  }
  const sheet = getDragSheet();
  sheet.classList.remove("is-dragging");
  const threshold = sheet.offsetHeight * 0.3;
  sheet.style.transform = "";
  if (dragCurrentY > threshold) {
    dragModalId === "modal" ? closeModal() : closeInfoModal();
  }
  dragStartY = null;
  dragCurrentY = 0;
  dragModalId = null;
});

function expose(name, fn) {
  exposed[name] = fn;
}

function getStored(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

const THEME_KEY = "theme";

function getStoredTheme() {
  return getStored(THEME_KEY);
}

function resolvedTheme() {
  const stored = getStoredTheme();
  if (stored === "dark" || stored === "light") {
    return stored;
  }
  return matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function syncTheme() {
  const theme = resolvedTheme();
  document.querySelectorAll(".theme-toggle").forEach((button) => {
    button.setAttribute("aria-pressed", String(theme === "dark"));
  });
  document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
    meta.setAttribute("content", theme === "dark" ? "#0f172a" : "#f9c412");
  });
}

function toggleTheme() {
  const next = resolvedTheme() === "dark" ? "light" : "dark";
  try {
    localStorage.setItem(THEME_KEY, next);
  } catch (e) {}
  document.documentElement.setAttribute("data-theme", next);
  syncTheme();
}
expose("toggleTheme", toggleTheme);

syncTheme();
matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
  if (!getStoredTheme()) {
    syncTheme();
  }
});

const TEXT_SIZE_KEY = "textSize";

function getStoredTextSize() {
  return getStored(TEXT_SIZE_KEY);
}

function syncTextSize() {
  document.querySelectorAll(".text-size-toggle").forEach((button) => {
    button.setAttribute("aria-pressed", String(getStoredTextSize() === "large"));
  });
}

function toggleTextSize() {
  const next = getStoredTextSize() === "large" ? "normal" : "large";
  try {
    localStorage.setItem(TEXT_SIZE_KEY, next);
  } catch (e) {}
  if (next === "large") {
    document.documentElement.setAttribute("data-text-size", "large");
  } else {
    document.documentElement.removeAttribute("data-text-size");
  }
  syncTextSize();
}
expose("toggleTextSize", toggleTextSize);

syncTextSize();

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}
expose("scrollToTop", scrollToTop);

addEventListener("click", (e) => {
  const handler = e.target.closest("[on-click]");
  if (!handler) {
    return;
  }
  e.preventDefault();
  const name = handler.getAttribute("on-click");
  const fn = exposed[name];
  if (!fn) {
    throw new Error("Unknown handler" + name);
  }
  fn(handler);
});

// There is a race condition here if an image loads faster than this JS file. But
// - that is unlikely
// - it only means potentially more costly layouts for that image.
// - And so it isn't worth the querySelectorAll it would cost to synchronously check
//   load state.
document.body.addEventListener(
  "load",
  (e) => {
    if (e.target.tagName != "IMG") {
      return;
    }
    // Ensure the browser doesn't try to draw the placeholder when the real image is present.
    e.target.style.backgroundImage = "none";
  },
  /* capture */ "true"
);



addEventListener("click", (e) => {
  const article = e.target.closest("article")

  if (!article || article.classList.contains('no-excerpt')) {
    return
  }
  e.preventDefault()

  openModal(article)
})

addEventListener("click", (e) => {
  const header = e.target.closest(".main-header")
  if (!header || e.target.closest("a, button, [on-click]")) {
    return
  }
  e.preventDefault()
  openInfoModal(header)
})

let currentActiveNavLink = null;

const changeNav = entries => {
  entries.forEach( entry => {
    // verify the element is intersecting
    if(entry.isIntersecting) {

      // get id of the intersecting section
      const id = entry.target.getAttribute('id')
      // find matching link & add appropriate class
      const active = document.querySelector(`[href="#${id}"]`)

      if(active !== currentActiveNavLink){

        if (currentActiveNavLink) {
          currentActiveNavLink.classList.remove('is-active')
        }

        active.classList.add('is-active')
        currentActiveNavLink = active
        scrollIntoView(active, {
          // behavior: 'smooth',
          // scrollMode: 'if-needed',
          inline: 'center',
          boundary: document.querySelector('.category-nav__list')
        })
      }
    }
  })
}

// init the observer
const optionsMenu = {
  root: null,
  rootMargin: '10% 0px -80% 0px',
  threshold: 1
}

const observerMenu = new IntersectionObserver(changeNav, optionsMenu)
// target the elements to be observed
document.querySelectorAll('h1[id]').forEach( section => {
  observerMenu.observe(section)
})


/**
  Sticky Menu
*/

// Which element to be fixed
const headerEl = document.querySelector('.category-nav')

// When this element disappears from visible area
const sentinalEl = document.querySelector('.sentinal')

const handler = (entries) => {
  // console.log(entries)
  // entries is an array of observed dom nodes
  // we're only interested in the first one at [0]
  // because that's our .sentinal node.
  // Here observe whether or not that node is in the viewport
  if (!entries[0].isIntersecting) {
    headerEl.classList.add('is-fixed')
  } else {
    headerEl.classList.remove('is-fixed')
  }
}

// create the observer
const observer = new window.IntersectionObserver(handler)
// give the observer some dom nodes to keep an eye on
observer.observe(sentinalEl)

// Keeps --category-nav-height in sync so the sticky search box (below) knows
// how far down to sit once the nav itself is pinned to the top.
function updateCategoryNavHeight() {
  document.documentElement.style.setProperty('--category-nav-height', `${headerEl.offsetHeight}px`)
}
updateCategoryNavHeight()
new ResizeObserver(updateCategoryNavHeight).observe(headerEl)

/**
  Menu search / filter
*/

const searchInput = document.getElementById('menuSearch')

if (searchInput) {
  const lang = document.body.getAttribute('data-lang') || 'tr'
  let fuse = null
  let searchItems = []
  let loadingPromise = null

  // Fuse's fuzzy matching works on the whole title string with no concept of
  // word boundaries, so a query like "tea" scores as a match against "Steak"
  // (it's a literal substring: s-TEA-k). Tokenizing into words and requiring
  // a word-prefix match avoids that; Fuse is kept only as a fallback for
  // actual typos, when no word-prefix match is found.
  function tokenize(text) {
    return text
      .toLocaleLowerCase(lang === 'tr' ? 'tr' : undefined)
      .split(/[^\p{L}\p{N}]+/u)
      .filter(Boolean)
  }

  function loadSearchIndex() {
    if (loadingPromise) {
      return loadingPromise
    }

    loadingPromise = fetch(`/search-index-${lang}.json`)
      .then((response) => response.json())
      .then((data) => {
        searchItems = data || []
        searchItems.forEach((item) => {
          item.words = tokenize([item.title, item.excerpt || '', ...(item.tags || []), ...(item.keywords || [])].join(' '))
        })
        fuse = new Fuse(searchItems, {
          keys: ['title', 'excerpt', 'tags', 'keywords'],
          threshold: 0.3,
        })
      })
      .catch((error) => {
        console.error('Failed to load search index:', error)
        loadingPromise = null
      })

    return loadingPromise
  }

  // Load search index on first focus or input
  searchInput.addEventListener('focus', () => {
    loadSearchIndex()
  }, { once: true })

  function navItemFor(section) {
    const heading = section.querySelector('h1[id]')
    const navLink = heading && document.querySelector(`.category-nav__link[href="#${heading.id}"]`)
    return navLink && navLink.closest('.category-nav__item')
  }

  // The menu DOM is static after load, so resolve every node the filter
  // touches once here instead of re-querying (and re-deriving each section's
  // nav item) on every keystroke.
  const allCards = [...document.querySelectorAll('[data-slug]')]
  const sectionModels = [...document.querySelectorAll('.menu-section')].map((section) => ({
    section,
    navItem: navItemFor(section),
    cards: [...section.querySelectorAll('[data-slug]')],
  }))

  function applyFilter(query) {
    const trimmed = query.trim()

    if (!trimmed || !fuse) {
      allCards.forEach((card) => { card.hidden = false })
      sectionModels.forEach(({ section, navItem }) => {
        section.hidden = false
        if (navItem) navItem.hidden = false
      })
      return
    }

    const normalizedQuery = trimmed.toLocaleLowerCase(lang === 'tr' ? 'tr' : undefined)
    const wordMatches = searchItems.filter((item) => item.words.some((word) => word.startsWith(normalizedQuery)))
    const matchedItems = wordMatches.length > 0 ? wordMatches : fuse.search(trimmed).map((result) => result.item)
    const matches = new Set(matchedItems.map((item) => item.slug))
    allCards.forEach((card) => {
      card.hidden = !matches.has(card.getAttribute('data-slug'))
    })
    sectionModels.forEach(({ section, navItem, cards }) => {
      const hasVisibleCard = cards.some((card) => !card.hidden)
      section.hidden = !hasVisibleCard
      if (navItem) navItem.hidden = !hasVisibleCard
    })
  }

  let filterTimeout
  searchInput.addEventListener('input', (e) => {
    clearTimeout(filterTimeout)
    const value = e.target.value
    filterTimeout = setTimeout(() => {
      loadSearchIndex().then(() => applyFilter(value))
    }, 150)
  })

  // Sticky search: only pin the box once it has scrolled behind the (already
  // sticky) category nav, and only while the user is actively searching
  // (focused or has a query) — otherwise it scrolls away like any section.
  const searchBoxEl = document.querySelector('.search-box')
  const searchSentinelEl = document.querySelector('.search-box__sentinel')

  if (searchBoxEl && searchSentinelEl) {
    let pastSearchSentinel = false

    // "Active" (focused or non-empty) is cheap to read on demand, so derive it
    // in place rather than mirroring it into a variable that needs syncing.
    function updateSearchSticky() {
      const active = document.activeElement === searchInput || searchInput.value.trim().length > 0
      searchBoxEl.classList.toggle('is-fixed', pastSearchSentinel && active)
    }

    searchInput.addEventListener('focus', updateSearchSticky)
    searchInput.addEventListener('blur', updateSearchSticky)
    searchInput.addEventListener('input', updateSearchSticky)

    const searchStickyObserver = new IntersectionObserver((entries) => {
      pastSearchSentinel = !entries[0].isIntersecting
      updateSearchSticky()
    }, {
      rootMargin: `-${headerEl.offsetHeight}px 0px 0px 0px`,
    })
    searchStickyObserver.observe(searchSentinelEl)
  }
}
